//@flow

// Vendors
import React, { useState, Suspense, lazy } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment';
import { TxButton, TxButtonMode, TxLoadingText } from 'texkit-ui/components';
import { useTranslation } from 'react-i18next';

// Components
import {
  FormActions,
  FormActionsItem,
  Trans,
  ServiceIcon,
  ServiceIconText,
} from '../..';
import { Modal } from '../../modals';

import SummaryCard from '../../Cards/SummaryCard/SummaryCard';

// Actions
import { closeModal } from '../../../../reducers/modal/modalActions';
import {
  unlinkAppLink,
  removeLinkFromState,
} from '../../../../reducers/agency/agencyActions';
import {
  pushMessageBanner,
  setAutoDismissMessageBannerTimer,
} from '../../../../reducers/messageBanner/messageBannerActions';

// Helpers
import { capitalize, toSentenceCase } from '../../../../lib/helpers';

// Config
import agenciesConfig from '../../../../agency-config/agencies';

// Styles
import './unlink-service-modal.scss';

// Types
type Props = {
  modal: Object,
  agencies: Object,
  closeModal: Function,
  unlinkAppLink: Function,
  removeLinkFromState: Function,
};

// Switch to adjusted SCSS if on iOS device
var userAgent = window.navigator.userAgent.toLowerCase(),
  ios = /iphone|ipod|ipad/.test(userAgent);

var mainClassName = ios ? 'unlink-service-modal-ios' : 'unlink-service-modal';

// PRJ1417943_IntergrationAccelerator - Dynamically lazy-load agency-specific SummaryCard components
const lazySummaryCards = agenciesConfig.reduce((acc, { agency }) => {
  const agencySlug = agency.agencyShortName;
  acc[agencySlug] = lazy(() =>
    import(`../../../../agencies/${agencySlug}/link-service/SummaryCard`)
  );
  return acc;
}, {});

const UnlinkServiceModal = ({
  unlinkAppLink,
  closeModal,
  removeLinkFromState,
  modal = {},
}: Props) => {
  const dispatch = useDispatch();
  const [isSubmitting, setSubmitting] = useState(false);
  const { t } = useTranslation('tpwd');
  if (
    modal.activeModal !== 'UnlinkServiceModal' ||
    !modal.data ||
    !modal.data.link ||
    !modal.data.agency
  )
    return null;

  const {
    data: { link, agency },
  } = modal;
  const {
    profile: {
      account: [accounts],
    },
  } = link;
  const { assetType = '' } = accounts;
  const assetTypeLC = assetType.toLowerCase();
  const hasLinks = agency.linkUserKeys && agency.linkUserKeys.length > 1;

  function handleRemoveLinkClick() {
    setSubmitting(true);

    const messageIdTime = Date.now();
    const timeString = moment().format('HH:mm:ss.0');

    const successBanner =
      hasLinks && assetType
        ? `${agency.agencySlug}_NoMoreAgencyNotificationsMessageBanner`
        : hasLinks
        ? `${agency.agencySlug}_unlinkNotificationMessageBanner`
        : agency.agencySlug !== 'dps'
        ? `${agency.agencySlug}_NoMoreAgencyNotificationsMessageBanner`
        : link.profile.account.length > 0 &&
          link.profile.account[0].licenseType === 'DL'
        ? `${agency.agencySlug}_NoMoreAgencyNotificationsMessageBanner_DL`
        : link.profile.account[0].licenseType === 'CDL'
        ? `${agency.agencySlug}_NoMoreAgencyNotificationsMessageBanner_CDL`
        : `${agency.agencySlug}_NoMoreAgencyNotificationsMessageBanner_ID`;

    const linkKey =
      agency.agencySlug === 'dps' && link.profile.account.length > 0
        ? link.profile.account[0].licenseType
        : modal.data.licenseNumber;

    unlinkAppLink(link, linkKey)
      .then(() => {
        dispatch(
          pushMessageBanner([
            {
              messageId: messageIdTime.toString(),
              textEnglish: `{"type": "Modals", "id": "${successBanner}", "args":"${assetTypeLC}"}`,
              textSpanish: `{"type": "Modals", "id": "${successBanner}", "args":"${assetTypeLC}"}`,
              startTime: `2021-01-01 ${timeString}`,
              endTime: '2040-06-30 12:05:00.0',
              priority: '4',
              status: 'R',
              author: 'USER',
              valid: true,
              dismissable: true,
            },
          ])
        );
        setTimeout(() => {
          removeLinkFromState(link);
          if (!hasLinks) {
            dispatch(push('/dashboard'));
          }
          closeModal('UnlinkServiceModal');
        }, 200);
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 300);
        setAutoDismissMessageBannerTimer(messageIdTime);
      })
      .catch(() => {
        dispatch(
          pushMessageBanner([
            {
              messageId: messageIdTime.toString(),
              textEnglish: `{"type": "Modals", "id": "${agency.agencySlug}_cantUnlink"}`,
              textSpanish: `{"type": "Modals", "id": "${agency.agencySlug}_cantUnlink"}`,
              startTime: '2021-01-01 18:35:00.0',
              endTime: '2040-06-30 12:05:00.0',
              priority: '1',
              status: 'R',
              author: 'USER',
              valid: true,
              dismissable: true,
            },
          ])
        );
        setSubmitting(false);
        closeModal('UnlinkServiceModal');
        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 300);
      });
  }

  // PRJ1417943_IntergrationAccelerator - Dynamically loads agency-specific SummaryCard (refactored from inline if/else blocks for maintainability)
  function getAgencySummaryCardComponent(agencySlug) {
    if (!agencySlug) return null;
    // eslint-disable-next-line import/no-dynamic-require
    return require(`../../../../agencies/${agencySlug}/link-service/SummaryCard`).default;
  }

  return (
    <Modal
      name="UnlinkServiceModal"
      className={`${mainClassName} ${agency.agencySlug}-modal`}
      onClose={() => setSubmitting(false)}
      disableOnClickOutside={true}
      disableCloseButton={false}
    >
      <h2 className="h1 modal-title">
        <Trans
          file="Modals"
          id={`${agency.agencySlug}_AreYouSureYouWantToRemoveThisLink`}
          args={[assetType.toLowerCase()]}
        />
      </h2>
      <div className="modal-content">
        <p className="lead">
          <Trans
            file="Modals"
            agency={agency}
            id={
              hasLinks
                ? `${agency.agencySlug}_unlinkNotification`
                : `${agency.agencySlug}_NoMoreAgencyNotifications`
            }
            args={[assetType]}
          />
        </p>
        <div className={`unlink-service-modal-list`}>
          <Suspense fallback={null}>
            {link.profile.account.map((account, idx) => {
              const agencySlug = agency.agencySlug
                ? agency.agencySlug.toUpperCase()
                : '';
              const AgencySummaryCard = lazySummaryCards[agencySlug];
              if (AgencySummaryCard) {
                return (
                  <AgencySummaryCard
                    key={idx}
                    account={account}
                    agency={agency}
                    t={t}
                    modalLicenseNumber={modal.data.licenseNumber}
                  />
                );
              }
              return '';
            })}
          </Suspense>
        </div>

        <FormActions align="center">
          <FormActionsItem>
            <TxButton
              className="ButtonDanger"
              onClick={handleRemoveLinkClick}
              mode={TxButtonMode.Danger}
            >
              {isSubmitting ? (
                <TxLoadingText>
                  <Trans file="Labels" id="Removing" fallback="Removing" />
                </TxLoadingText>
              ) : (
                <Trans file="Labels" id="Remove" />
              )}
            </TxButton>
          </FormActionsItem>

          <FormActionsItem>
            <TxButton
              className="ButtonPrimary"
              onClick={() => closeModal('UnlinkServiceModal')}
              mode={TxButtonMode.Secondary}
              disabled={isSubmitting}
            >
              <Trans file="Labels" id="Cancel" fallback="Cancel" />
            </TxButton>
          </FormActionsItem>
        </FormActions>
      </div>
    </Modal>
  );
};

const mapDispatchToProps = (dispatch: Object) =>
  bindActionCreators(
    { closeModal, unlinkAppLink, removeLinkFromState },
    dispatch
  );
const mapStateToProps = ({ modal }): Object => ({ modal });

export default connect(mapStateToProps, mapDispatchToProps)(UnlinkServiceModal);
